<script setup lang="ts">
import { isAppUsing } from '@@/bits/flip'
import { __ } from '@@/bits/intl'
import { WallWishGroupByValue } from '@@/enums'
import OzContainedButton from '@@/library/v4/components/OzContainedButton.vue'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfacePostGroupByStore } from '@@/pinia/surface_post_group_by_store'
import { useSurfacePostPropertiesStore } from '@@/pinia/surface_post_properties'
import { useSurfaceSectionsStore } from '@@/pinia/surface_sections'
import { useSurfaceWishArrangementStore } from '@@/pinia/surface_wish_arrangement'
import { storeToRefs } from 'pinia'
import { ref } from 'vue'

const { backgroundColor, backgroundLuminance, colorScheme, isMap, isTimelineV1, skinPrimaryVariantColorAsRGB } =
  storeToRefs(useSurfaceStore())
const surfaceSectionsStore = useSurfaceSectionsStore()
const surfaceWishArrangementStore = useSurfaceWishArrangementStore()
const surfacePostGroupByStore = useSurfacePostGroupByStore()
const surfacePostPropertiesStore = useSurfacePostPropertiesStore()

const disabled = ref(false)

async function createNewSection(): Promise<void> {
  disabled.value = true

  if (surfaceWishArrangementStore.isGroupBySingleSelect) {
    const newOptionId = await surfacePostPropertiesStore.createSingleSelectPropertyOptionBefore({
      propertyId: surfacePostGroupByStore.currentSingleSelectProperty?.id ?? undefined,
      optionId: WallWishGroupByValue.NoValue,
    })
    if (newOptionId == null) {
      disabled.value = false
      return
    }
    surfacePostGroupByStore.singleSelectPropertyOptionRecentlyCreatedId = newOptionId
    surfacePostGroupByStore.startRenamingGroup(newOptionId)
  } else {
    const createSectionOptions = {
      promptRenameSection: true,
    }
    await surfaceSectionsStore.createSectionWithDefaultName(createSectionOptions)
  }

  disabled.value = false
}
</script>

<template>
  <OzContainedButton
    :class="[
      'flex items-center justify-center',
      'rounded-xl',
      'px-3',
      {
        'py-1.5': !isMap,
        'py-0.875': isMap,
      },

      // Background
      isMap && {
        'bg-grey-100 hover-hover:hover:bg-grey-200': backgroundLuminance === 'light',
        'bg-grey-700 hover-hover:hover:bg-grey-600': backgroundLuminance === 'dark',
      },
      isTimelineV1 && {
        'backdrop-blur-12': true,
        'bg-[rgba(255,255,255,0.32)] hhover:bg-light-ui-100': backgroundLuminance === 'light',
        'bg-[rgba(0,0,0,0.24)] hhover:bg-dark-ui-100': backgroundLuminance === 'dark',
      },
      // For non-map and non-timeline formats, the background color is set by the style attribute.
      // Additionally, the opacity is set to 90%.
      !isMap && !isTimelineV1 && 'opacity-90',

      // Text
      {
        'text-body-small font-semibold': !isMap,
        'text-body-extra-small font-semibold': isMap,
      },
      {
        'text-dark-text-200': backgroundLuminance === 'light',
        'text-light-text-200': backgroundLuminance === 'dark',
      },

      !disabled && [
        // Background
        'transition-opacity motion-reduce:transition-none',
        !isMap && 'hover-hover:hover:opacity-100',
        {
          'hover-hover:active:!bg-grape-500':
            isAppUsing('returnOfTimeline') && isTimelineV1 ? colorScheme === 'light' : backgroundLuminance === 'light',
          'hover-hover:active:!bg-canary-500':
            isAppUsing('returnOfTimeline') && isTimelineV1 ? colorScheme === 'dark' : backgroundLuminance === 'dark',
        },

        // Text
        {
          'hover-hover:active:text-light-text-200': backgroundLuminance === 'light',
          'hover-hover:active:text-dark-text-200': backgroundLuminance === 'dark',
        },

        // Focus ring
        'focus-visible:outline-none',
        'focus-visible:ring-[2.5px]',
        {
          'ring-grape-500':
            isAppUsing('returnOfTimeline') && isTimelineV1 ? colorScheme === 'light' : backgroundLuminance === 'light',
          'ring-canary-500':
            isAppUsing('returnOfTimeline') && isTimelineV1 ? colorScheme === 'dark' : backgroundLuminance === 'dark',
        },
      ],
    ]"
    :style="
      !isMap &&
      !isTimelineV1 && {
        backgroundColor: skinPrimaryVariantColorAsRGB || backgroundColor,
      }
    "
    :color-scheme="null"
    :size-preset="null"
    :dark-mode="backgroundLuminance === 'dark'"
    :text="disabled ? __('Adding section...') : __('Add section')"
    :disabled="disabled"
    data-testid="surfaceAddSectionButton"
    @click="createNewSection"
  />
</template>
